import { Flex, Spin, Typography } from 'antd'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CommonModal from 'src/app/shared/components/modal/common-modal'
import {
  AchExceptionDefault,
  CheckExceptionDefault,
  EXCEPTION_ACH_TYPE_MAPPING,
  EXCEPTION_CHECK_TYPE_MAPPING,
} from '../constants'
import ExceptionStatusTag from '../ExceptionStatusTag'
import ButtonGroup from './ButtonGroup'
import { getACHExceptionDetail, getExceptionDetail } from 'src/app/shared/services/TransactionService'

import './index.scss'
import { ACH_EXCEPTION_DETAIL, CHECK_EXCEPTION_DETAIL } from '../constants/index.model'
import { useSelector } from 'react-redux'
import { getUserProfile } from 'src/app/store/auth'
import { USER_ROLE } from 'src/app/shared/constants/app.constants'
import { getDefaultTimeZone } from 'src/app/store/masterSetup'
import { formatAmountValue, getMappingInfo } from '../utils'
import { useForm } from 'react-hook-form'
import ExceptionInfo from './ExceptionInfo'
import CheckImage from './CheckImage'
import AddACHModal from '../add-ach-modal'

interface Props {
  isOpen: boolean
  onClose: () => void
  loading: boolean
  exceptionType: string
  handleClickNext: () => void
  handleClickPrevious: () => void
  handlePay: () => void
  handleReturn: () => void
  selectedId: string
  selectedIdIndex: number
  api: any
  isPaying: boolean
  isReturning: boolean
  contextFilteredIdList: string[]
  navigationContext: 'EXCEPTION' | 'DECISIONED' | null
}

function ViewExceptionDetailModal(props: Props) {
  const { t } = useTranslation()
  const {
    isOpen,
    onClose,
    exceptionType,
    handleClickNext,
    handleClickPrevious,
    selectedId,
    selectedIdIndex,
    handlePay,
    handleReturn,
    api,
    isPaying,
    isReturning,
    contextFilteredIdList,
    navigationContext,
  } = props

  const [checkExceptionDetail, setCheckExceptionDetail] = useState<CHECK_EXCEPTION_DETAIL>(CheckExceptionDefault)
  const [achExceptionDetail, setAchExceptionDetail] = useState<ACH_EXCEPTION_DETAIL>(AchExceptionDefault)
  const [isFetchingException, setIsFetchingException] = useState(false)
  const userProfile = useSelector(getUserProfile)
  const defaultTimeZone = useSelector(getDefaultTimeZone)

  const isACH = exceptionType?.includes('ACH')

  const { control, reset } = useForm<any>({
    mode: 'all',
    defaultValues: isACH ? achExceptionDetail : checkExceptionDetail,
  })

  const getCheckException = useCallback(async () => {
    if (selectedId) {
      try {
        setIsFetchingException(true)
        const timezoneID =
          userProfile?.role === USER_ROLE.CLIENT ? userProfile?.client?.timeZoneId : defaultTimeZone?.code
        if (timezoneID) {
          const res = await getExceptionDetail(selectedId, timezoneID)

          setCheckExceptionDetail(res?.data)

          reset(res?.data)

          return res?.data
        }
        return
      } catch (error: any) {
        console.log('Error', error)
      } finally {
        setIsFetchingException(false)
      }
    }
  }, [defaultTimeZone, userProfile, setCheckExceptionDetail, selectedId, reset])

  const getDetailACHException = useCallback(async () => {
    if (selectedId) {
      try {
        setIsFetchingException(true)
        const timezoneID =
          userProfile?.role === USER_ROLE.CLIENT ? userProfile?.client?.timeZoneId : defaultTimeZone?.code
        if (timezoneID) {
          const res = await getACHExceptionDetail(selectedId, timezoneID)

          setAchExceptionDetail(res?.data)

          reset(res?.data)

          return res?.data
        }
        return
      } catch (error: any) {
        console.log('Error', error)
      } finally {
        setIsFetchingException(false)
      }
    }
  }, [defaultTimeZone, userProfile, setAchExceptionDetail, selectedId, reset])

  useEffect(() => {
    if (exceptionType) {
      if (isACH) {
        getDetailACHException()
        return
      }
      getCheckException()
    }
  }, [getCheckException, isACH, getDetailACHException, exceptionType])

  const [addRuleModalOpen, setAddRuleModalOpen] = useState(false)

  const handleAddRule = () => {
    setAddRuleModalOpen(true)
  }

  return (
    <CommonModal
      title={t('QuickExceptionProcessing:modal.title')}
      size='large'
      open={isOpen}
      onClose={onClose}
      footer={[
        <ButtonGroup
          onClose={onClose}
          handlePay={handlePay}
          handleReturn={handleReturn}
          handleClickNext={handleClickNext}
          handleAddRule={handleAddRule}
          handleClickPrevious={handleClickPrevious}
          selectedId={selectedIdIndex}
          isFetchingException={isFetchingException}
          exceptionStatus={isACH ? achExceptionDetail?.exceptionStatus : checkExceptionDetail?.exceptionStatus}
          isACH={isACH}
          isOverdue={isACH ? achExceptionDetail?.overdue : checkExceptionDetail?.overdue}
          isPaying={isPaying}
          isReturning={isReturning}
          contextFilteredIdList={contextFilteredIdList}
          currentExceptionId={selectedId}
          navigationContext={navigationContext}
        />,
      ]}
    >
      <div className='exceptionDetailModal'>
        {!isFetchingException ? (
          <>
            <div className='detailCheckHeader'>
              <Flex justify='space-between'>
                <Typography className='headerName'>
                  {getMappingInfo(
                    isACH ? achExceptionDetail?.exceptionType : checkExceptionDetail?.exceptionType,
                    isACH ? EXCEPTION_ACH_TYPE_MAPPING : EXCEPTION_CHECK_TYPE_MAPPING,
                  )}
                </Typography>
              </Flex>
              <Flex gap={12} align='center'>
                <Typography className='amount'>
                  {formatAmountValue(isACH ? achExceptionDetail?.amount : checkExceptionDetail?.amount)}
                </Typography>
                <ExceptionStatusTag
                  status={isACH ? achExceptionDetail?.exceptionStatus : checkExceptionDetail?.exceptionStatus}
                />
              </Flex>
            </div>

            <ExceptionInfo
              exceptionType={exceptionType}
              control={control}
              exceptionStatus={isACH ? achExceptionDetail?.exceptionStatus : checkExceptionDetail?.exceptionStatus}
              isACH={isACH}
              hasIssuedCheckFile = {checkExceptionDetail.issuedCheckFileId != null}
            />

            {!isACH && checkExceptionDetail?.frontImagePath && checkExceptionDetail?.backImagePath && (
              <div className='detailCheckImage'>
                <CheckImage checkExceptionDetail={checkExceptionDetail} />
              </div>
            )}
          </>
        ) : (
          <Spin />
        )}
      </div>

      <AddACHModal
        isOpen={addRuleModalOpen}
        onClose={() => setAddRuleModalOpen(false)}
        closeDetailCheckPanel={() => {}}
        achPlaceholders={{
          clientNames: achExceptionDetail?.clientId,
          accountNames: [achExceptionDetail?.accountId],
          achOriginatorId: achExceptionDetail?.achOriginatorID,
          secCode:
            achExceptionDetail?.secCode && achExceptionDetail?.secCode !== '-' ? achExceptionDetail?.secCode : null,
          creditOrDebit: achExceptionDetail?.debitOrCredit,
          amount: achExceptionDetail?.amount,
        }}
        api={api}
      />
    </CommonModal>
  )
}

export default ViewExceptionDetailModal
